import { Controller } from "@hotwired/stimulus";

// Keeps the preview pane in step with the form. Every edit is mirrored into
// the preview at once (title, summary, body, and a cover picked from disk),
// then, after a short pause, the form is posted to the preview action and the
// server's rendering replaces the mirror. The cover file stays out of that
// request (it can be megabytes; the local file already shows) and _method is
// dropped so the POST stays a POST.
export default class extends Controller {
  static targets = ["form", "frame", "coverTemplate"];
  static values = { url: String, debounce: { type: Number, default: 250 } };

  connect() {
    this.requests = 0;
    this.edits = 0;
  }

  disconnect() {
    clearTimeout(this.timer);
    this.dropCover();
  }

  schedule() {
    this.edits += 1;
    this.mirror();
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), this.debounceValue);
  }

  coverChanged(event) {
    this.dropCover();
    const [file] = event.target.files;
    if (file) this.coverUrl = URL.createObjectURL(file);
    this.applyCover();
  }

  async refresh() {
    const body = new FormData(this.formTarget);
    body.delete("changelog_entry[cover_image]");
    body.delete("_method");
    const request = ++this.requests;
    const edits = this.edits;

    const response = await fetch(this.urlValue, {
      method: "POST",
      body,
      headers: {
        Accept: "text/html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
      },
    });
    // A slow earlier response must not overwrite a newer one.
    if (!response.ok || request !== this.requests) return;

    // The swap replaces the scroll container too, so carry its position over.
    const scrollTop = this.scroller?.scrollTop ?? 0;
    this.frameTarget.innerHTML = await response.text();
    if (this.scroller) this.scroller.scrollTop = scrollTop;
    if (this.edits !== edits) this.mirror();
    this.applyCover();
  }

  get scroller() {
    return this.frameTarget.querySelector("[data-live-preview-scroll]");
  }

  // Straight from the form into the rendered preview, so typing never waits
  // on the network. The server render that follows is the canonical version.
  mirror() {
    const data = new FormData(this.formTarget);
    const frame = this.frameTarget;
    const title = (data.get("changelog_entry[title]") || "").trim();
    const excerpt = (data.get("changelog_entry[excerpt]") || "").trim();

    const heading = frame.querySelector("article h1");
    if (heading) heading.textContent = title;
    for (const [field, section] of [["content", "body"], ["improvements", "improvements"], ["fixes", "fixes"]]) {
      const target = frame.querySelector(`[data-changelog-section="${section}"] .rich-text-content`);
      if (target) target.innerHTML = this.renderable(data.get(`changelog_entry[${field}]`) || "");
    }

    const cardTitle = frame.querySelector('[data-live-preview-mirror="card-title"]');
    if (cardTitle) cardTitle.textContent = title || cardTitle.dataset.empty;
    const cardSubtext = frame.querySelector('[data-live-preview-mirror="card-subtext"]');
    if (cardSubtext) {
      cardSubtext.textContent = excerpt.length > 120 ? `${excerpt.slice(0, 117)}...` : excerpt;
      cardSubtext.hidden = excerpt === "";
    }
  }

  // The editor's HTML with each image attachment stood in by a plain figure;
  // anything else attached is left to the server render.
  renderable(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("action-text-attachment").forEach((node) => {
      const url = node.getAttribute("url");
      if (url && (node.getAttribute("content-type") || "").startsWith("image/")) {
        const figure = doc.createElement("figure");
        figure.className = "attachment attachment--preview";
        const img = doc.createElement("img");
        img.src = url;
        img.alt = "";
        figure.append(img);
        node.replaceWith(figure);
      } else {
        node.remove();
      }
    });
    return doc.body.innerHTML;
  }

  applyCover() {
    if (!this.coverUrl) return;
    const article = this.frameTarget.querySelector("article");
    if (!article) return;

    let cover = article.querySelector(".changelog-cover");
    if (!cover) {
      cover = this.coverTemplateTarget.content.firstElementChild.cloneNode(true);
      article.querySelector("h1").after(cover);
    }
    const image = cover.querySelector("img");
    ["srcset", "width", "height"].forEach((attribute) => image.removeAttribute(attribute));
    image.classList.remove("opacity-0");
    cover.classList.remove("skeleton");
    image.src = this.coverUrl;
  }

  dropCover() {
    if (this.coverUrl) URL.revokeObjectURL(this.coverUrl);
    this.coverUrl = null;
  }
}
